import React, { useState } from 'react';
import { AppShell } from '@/components';
import { MarketingSection, SectionHeader, GlassPanel } from '@/components/marketing';
import styles from './Solutions.module.css';

const personaOptions = [
  {
    key: 'platform',
    label: 'Platform & SRE',
    headline: 'Keep multi-cloud serverless estates compliant and observable.',
    body: 'Relay unifies namespace policy, execution telemetry, and incident tooling so your platform team stays ahead of latency spikes and policy drift without juggling bespoke dashboards.',
    metrics: [
      { label: 'Latency budget adherence', value: '98%' },
      { label: 'Policy rollouts', value: '<5 min' },
      { label: 'Supported clouds', value: 'AWS • Cloudflare • Vercel' },
    ],
    cta: { label: 'Book an architecture review', href: '/contact' },
  },
  {
    key: 'product',
    label: 'Product & Growth',
    headline: 'Package reusable functions into products with usage-led revenue.',
    body: 'Define pricing catalogs, entitlement workflows, and payouts without custom tooling. Relay’s ledger keeps consumption, refunds, and reseller splits auditable from day one.',
    metrics: [
      { label: 'Time to launch a plan', value: 'Under 1 day' },
      { label: 'Billing models', value: 'Usage • Subscription • Hybrid' },
      { label: 'Revenue visibility', value: 'Real-time dashboards' },
    ],
    cta: { label: 'Download the pricing playbook', href: '/resources/pricing-playbook' },
  },
  {
    key: 'engineering',
    label: 'Application Engineering',
    headline: 'Ship faster with guardrails that never fight velocity.',
    body: 'Publish, test, and promote functions across environments with branch-aware RUFIDs, instant rollbacks, and studio previews. Relay keeps change reviews and telemetry in the same control plane.',
    metrics: [
      { label: 'Deploy confidence', value: 'Version graph + audit trail' },
      { label: 'Rollback time', value: '<30 seconds' },
      { label: 'IDE integrations', value: 'VS Code • JetBrains' },
    ],
    cta: { label: 'Explore Studio workflows', href: '/studio' },
  },
  {
    key: 'executive',
    label: 'Executive & Finance',
    headline: 'Prove value with cost, compliance, and revenue telemetry in one pane.',
    body: 'Relay surfaces the business view of your serverless estate—cost allocation, compliance readiness, partner revenue, and SLA posture—without waiting on stitched spreadsheets.',
    metrics: [
      { label: 'Cost coverage', value: 'All clouds & regions' },
      { label: 'Compliance posture', value: 'SOC2-ready controls' },
      { label: 'Reporting cadence', value: 'Weekly scorecards' },
    ],
    cta: { label: 'Request executive briefing', href: '/contact' },
  },
];

const SolutionsPage: React.FC = () => {
  const [activePersona, setActivePersona] = useState(personaOptions[0]);

  return (
    <AppShell>
      <div className={styles.page}>
        <MarketingSection theme="gradient" collapsePadding>
          <div className={styles.heroGrid}>
            <div className="space-y-6">
              <SectionHeader
                overline="Solutions"
                title="Tailored control-plane outcomes for every team that touches serverless"
                description="Select your track to see how Relay unlocks operational control, monetization, and compliance across clouds—without sacrificing velocity."
              />
            </div>
            <GlassPanel padding="lg" className={styles.personaList}>
              {personaOptions.map((persona) => (
                <button
                  key={persona.key}
                  onClick={() => setActivePersona(persona)}
                  className={`${styles.personaCard} ${
                    activePersona.key === persona.key
                      ? 'border-emerald-500 bg-emerald-500/15 text-white shadow-lg shadow-emerald-500/20'
                      : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/30 hover:bg-white/10'
                  }`}
                >
                  <span className="font-medium">{persona.label}</span>
                  <span className="text-xs uppercase tracking-wide text-slate-400">View</span>
                </button>
              ))}
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <div className={styles.personaGrid}>
            <div className="space-y-8">
              <SectionHeader title={activePersona.headline} description={activePersona.body} />
              <div className={styles.metricGrid}>
                {activePersona.metrics.map((metric) => (
                  <GlassPanel key={metric.label} padding="sm">
                    <p className="text-xs uppercase tracking-wide text-slate-400">{metric.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-white">{metric.value}</p>
                  </GlassPanel>
                ))}
              </div>
              <a
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-400"
                href={activePersona.cta.href}
              >
                {activePersona.cta.label}
              </a>
            </div>
            <div className="space-y-6">
              <GlassPanel>
                <h3 className="text-lg font-semibold text-white">Featured capabilities</h3>
                <ul className="mt-4 space-y-3 text-sm text-slate-300">
                  <li>• Namespace policy automation &amp; guardrails</li>
                  <li>• Budget enforcement tied to execution telemetry</li>
                  <li>• Cross-cloud deployment orchestration</li>
                  <li>• Revenue analytics &amp; entitlement management</li>
                </ul>
              </GlassPanel>
              <GlassPanel>
                <h3 className="text-lg font-semibold text-white">How teams adopt Relay</h3>
                <ol className="mt-4 space-y-3 text-sm text-slate-300">
                  <li><span className="font-semibold text-white">1.</span> Inventory functions and map RUFIDs.</li>
                  <li><span className="font-semibold text-white">2.</span> Configure namespaces, policies, and telemetry budgets.</li>
                  <li><span className="font-semibold text-white">3.</span> Roll out marketplace &amp; monetization workflows.</li>
                </ol>
              </GlassPanel>
            </div>
          </div>
        </MarketingSection>

        <MarketingSection theme="deep">
          <SectionHeader
            overline="Industry playbooks"
            title="Blueprints tailored to your vertical"
            description="Relay meets teams where they operate. Explore ready-made collections that combine policies, telemetry, and monetization patterns tailored to your industry."
          />
          <div className={`${styles.industryGrid} mt-12`}>
            <GlassPanel>
              <p className="text-white">Fintech providers</p>
              <p className="mt-2 text-sm text-slate-300">PCI-aware namespaces, audit exports, revenue share workflows.</p>
            </GlassPanel>
            <GlassPanel>
              <p className="text-white">Commerce platforms</p>
              <p className="mt-2 text-sm text-slate-300">Low-latency checkout functions, global cache profiles, partner billing.</p>
            </GlassPanel>
            <GlassPanel>
              <p className="text-white">AI &amp; automation</p>
              <p className="mt-2 text-sm text-slate-300">GPU-aware budgets, usage analytics, workflow orchestration.</p>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <GlassPanel padding="lg" className={styles.testimonial}>
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">Relay keeps cross-functional teams in sync</h2>
            <p className="text-base text-slate-300">
              “Relay let us consolidate four bespoke systems into one policy-aware control plane. Latency stayed under target while monetization went live in the same quarter.”
            </p>
            <div className={`${styles.testimonialFooter} text-sm text-slate-400`}>
              <div>
                <p className="font-semibold text-white">Sierra Patel</p>
                <p>Director of Platform, AlloyEdge</p>
              </div>
              <span className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 py-1 text-xs uppercase tracking-wide text-emerald-200">
                42% faster launch
              </span>
            </div>
          </GlassPanel>
        </MarketingSection>
      </div>
    </AppShell>
  );
};

export default SolutionsPage;
